// @flow

import * as React from "react";
import _values from "lodash/values";
import _max from "lodash/max";
import _debounce from "lodash/debounce";
import cx from "classnames";

import styles from "./FormRow.scss";
import Separator from "../Separator";

type FormRowProps = {
  children?: React.Node,
  isWithoutPadding: boolean,
  separator: boolean,
  spacing: "md" | "lg",
  className?: string,
  separatorPosition: "top" | "bottom",
  paddingPosition: "top" | "bottom"
};

const setHeadersHeight = rowRef => {
  const rowEl = rowRef && rowRef.current;

  if (rowEl) {
    const headerElements = _values(rowEl.querySelectorAll("[data-header]"));
    const heights = Array.isArray(headerElements) ? headerElements.map(el => el.offsetHeight) : [0];
    const highest = _max(heights);

    headerElements.forEach(el => {
      const elCopy = el;

      if (elCopy && elCopy.style) {
        elCopy.style.minHeight = `${highest}px`;
      }
    });
  }
};

const useSetupHeadersHeightCalculations = rowRef => {
  const handleWindowResize = _debounce(() => setHeadersHeight(rowRef), 300);

  React.useEffect(() => {
    handleWindowResize();
    window.addEventListener("resize", handleWindowResize);

    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  return null;
};

const FormRow = ({
  children,
  isWithoutPadding,
  separator,
  spacing,
  className,
  separatorPosition,
  paddingPosition
}: FormRowProps) => {
  const rowRef = React.useRef(null);

  useSetupHeadersHeightCalculations(rowRef);

  return (
    <React.Fragment>
      {separator && separatorPosition === "top" && (
        <Separator separatorPosition={separatorPosition} />
      )}
      <div
        className={cx(
          {
            [styles.formRowContainer]: true,
            [styles.hasSeparator]: separator,
            [styles.withoutPadding]: isWithoutPadding,
            [styles.paddingTop]: paddingPosition === "top",
            [styles[`spacing-${spacing}`]]: true
          },
          className
        )}
        data-testid="formrow"
        ref={rowRef}
      >
        {children}
      </div>
      {separator && separatorPosition === "bottom" && (
        <Separator separatorPosition={separatorPosition} />
      )}
    </React.Fragment>
  );
};

FormRow.defaultProps = {
  isWithoutPadding: false,
  separator: false,
  spacing: "md",
  separatorPosition: "bottom",
  paddingPosition: "bottom"
};

export default FormRow;
